import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { AccountStatus, AccountStatusEntry } from '../types'

interface Props {
  history: AccountStatusEntry[]
}

const TONE: Record<AccountStatus, string> = {
  active: 'bg-status-success',
  dormant: 'bg-status-pending',
  closed: 'bg-muted-fg/60',
}

const LABEL: Record<AccountStatus, string> = {
  active: 'Active',
  dormant: 'Dormant',
  closed: 'Closed',
}

/**
 * AF-132 — vertical timeline of every status change for the account.
 * PND apply/lift events are folded in even when the account-level
 * `status` itself didn't change; the from/to are equal in that case
 * but the reason text carries the PND context.
 */
export function AccountStatusTimeline({ history }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Status history</CardTitle>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-sm text-muted-fg">No status events yet.</p>
        ) : (
          <ol className="relative flex flex-col gap-3 border-l border-border pl-4">
            {history.map((entry) => (
              <li key={entry.id} className="relative">
                <span
                  aria-hidden
                  className={cn(
                    'absolute -left-[1.32rem] top-1.5 h-2 w-2 rounded-full ring-4 ring-card',
                    TONE[entry.to_status],
                  )}
                />
                <p className="text-sm">
                  <span className="font-medium">{LABEL[entry.to_status]}</span>
                  {entry.from_status && entry.from_status !== entry.to_status && (
                    <span className="text-muted-fg">
                      {' '}from {LABEL[entry.from_status]}
                    </span>
                  )}
                  {entry.by && (
                    <span className="text-muted-fg"> by {entry.by.display_name}</span>
                  )}
                </p>
                {entry.reason && (
                  <p className="mt-0.5 text-xs italic text-muted-fg">{entry.reason}</p>
                )}
                <p className="mt-0.5 text-xs text-muted-fg">{formatDateTime(entry.at)}</p>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}
